// src/router/FirebaseInitializer.jsx

import React, { useState, useEffect } from 'react';
import firebaseInstance from '../services/firebaseSingleton.jsx';
import LoadingScreen from '../components/common/LoadingScreen.jsx';
import { getApps } from 'firebase/app';

/**
 * This component acts as a gatekeeper. It ensures that the main application
 * (its children) is only rendered AFTER Firebase has been successfully initialized.
 * This prevents race conditions and errors throughout the entire app.
 */
const FirebaseInitializer = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(firebaseInstance.isInitialized());
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      // If already initialized (e.g., by another part of the app or on a hot reload), do nothing.
      if (isInitialized || getApps().length > 0) {
        if (!isInitialized) setIsInitialized(true);
        return;
      }

      try {
        const firebaseConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
        };

        if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
          throw new Error("Firebase configuration variables (VITE_FIREBASE_*) are missing from your .env file.");
        }

        await firebaseInstance.initialize(firebaseConfig);

        if (isMounted) {
          setIsInitialized(true);
        }
      } catch (err) {
        console.error("FATAL: Firebase Initialization Failed:", err);
        if (isMounted) {
          setError(err.message || 'An unknown error occurred during Firebase initialization.');
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [isInitialized]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-lg w-full text-center">
          <h2 className="text-red-700 font-bold text-xl mb-4">Erreur Critique d'Initialisation</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-medium"
          >
            Recharger la Page
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return <LoadingScreen type="default" customMessage="Initialisation des services de base..." />;
  }

  return children;
};

export default FirebaseInitializer;